import React from 'react';
import styled from 'styled-components';
import { UnifreightDocumentUploadSummary } from '../../../models/entities/shipment/unifreightIntegration';
import theme from '../Theme/theme';

interface Props {
    visible: boolean;
    fileName: string;
    shipmentNumber: string;
    message: string;
    validationSkipped: boolean;
    documents?: UnifreightDocumentUploadSummary;
    onClose: () => void;
    localization: any;
}

const UnifreightSuccessModal: React.FC<Props> = ({
    visible,
    fileName,
    shipmentNumber,
    message,
    validationSkipped,
    documents,
    onClose,
    localization
}) => {
    if (!visible) return null;

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <HeaderTitle>
                        <SuccessIcon className="material-icons">check_circle</SuccessIcon>
                        {localization.unifreight_success_title || 'Success'}
                    </HeaderTitle>
                    <CloseButton onClick={onClose}>
                        <span className="material-icons">close</span>
                    </CloseButton>
                </ModalHeader>

                <ModalContent>
                    <SuccessMessage>{message || 'Shipment sent to Unifreight successfully!'}</SuccessMessage>

                    <DetailsContainer>
                        <DetailRow>
                            <DetailLabel>{localization.unifreight_shipment_number || 'Shipment Number'}:</DetailLabel>
                            <DetailValue>{shipmentNumber}</DetailValue>
                        </DetailRow>

                        <DetailRow>
                            <DetailLabel>{localization.unifreight_file_name || 'File Name'}:</DetailLabel>
                            <DetailValue>{fileName}</DetailValue>
                        </DetailRow>

                        {documents && (
                            <DetailRow>
                                <DetailLabel>{localization.unifreight_documents_sent || 'Documents Sent'}:</DetailLabel>
                                <DetailValue>
                                    {documents.uploaded}
                                    {documents.skipped > 0 ? ` (${documents.skipped} skipped)` : ''}
                                    {documents.failed > 0 ? ` (${documents.failed} failed)` : ''}
                                </DetailValue>
                            </DetailRow>
                        )}

                        {validationSkipped && (
                            <WarningBanner>
                                <WarningIcon className="material-icons">warning</WarningIcon>
                                <WarningText>
                                    {localization.unifreight_validation_skipped ||
                                        'Note: Validation was skipped for this shipment (development mode).'}
                                </WarningText>
                            </WarningBanner>
                        )}
                    </DetailsContainer>
                </ModalContent>

                <ModalFooter>
                    <CloseButtonStyled onClick={onClose}>{localization.close || 'Close'}</CloseButtonStyled>
                </ModalFooter>
            </ModalContainer>
        </Overlay>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.75);
    z-index: 1201;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ModalContainer = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 0 18px 15px rgba(0, 0, 0, 0.28);
    width: 90%;
    max-width: 550px;
    display: flex;
    flex-direction: column;
    animation: slideUp 0.3s ease-out;

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 30px;
    border-bottom: 1px solid #e0e0e0;
`;

const HeaderTitle = styled.div`
    display: flex;
    align-items: center;
    font-size: 20px;
    font-weight: bold;
    color: ${theme.colors.pageTitle};
`;

const SuccessIcon = styled.span`
    color: #4caf50;
    margin-right: 10px;
    font-size: 28px !important;
`;

const CloseButton = styled.div`
    cursor: pointer;
    color: #666;
    display: flex;
    align-items: center;

    &:hover {
        color: #333;
    }

    span {
        font-size: 24px;
    }
`;

const ModalContent = styled.div`
    padding: 25px 30px;
`;

const SuccessMessage = styled.div`
    background-color: #e8f5e9;
    border-left: 4px solid #4caf50;
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 4px;
    color: #2e7d32;
    font-weight: 500;
`;

const DetailsContainer = styled.div`
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 20px;
`;

const DetailRow = styled.div`
    display: flex;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const DetailLabel = styled.div`
    font-weight: 600;
    color: #616161;
    min-width: 140px;
`;

const DetailValue = styled.div`
    color: #212121;
    word-break: break-all;
`;

const WarningBanner = styled.div`
    background-color: #fff3e0;
    border-left: 4px solid #ff9800;
    padding: 12px;
    margin-top: 15px;
    border-radius: 4px;
    display: flex;
    align-items: flex-start;
`;

const WarningIcon = styled.span`
    color: #f57c00;
    font-size: 20px !important;
    margin-right: 8px;
    margin-top: 2px;
`;

const WarningText = styled.div`
    color: #e65100;
    font-size: 13px;
    line-height: 1.4;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 15px 30px;
    border-top: 1px solid #e0e0e0;
`;

const CloseButtonStyled = styled.button`
    background-color: #4caf50;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: background-color 0.2s;

    &:hover {
        background-color: #388e3c;
    }

    &:active {
        background-color: #2e7d32;
    }
`;

export default UnifreightSuccessModal;
